import React, { useState, useEffect, useRef } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { getSessionPosition, hourAxisLabel } from './TimeGrid';
import SessionCard from './SessionCard';
import Header from '../shared/Header';
import SessionModal from '../shared/SessionModal.jsx';
import SpeakerModal from '../shared/SpeakerModal.jsx';
import { getSessionDataForModal, getSpeakerDataForModal, groupOverlappingSessions } from './utils.js';
import { WIDGET_TYPES, MODAL_TYPES } from '../constants.js';
import getVisibleFieldIdsOfType, { getVisibleFieldsOfTypeById } from '../utils/get-visible-fields-of-widget.js';
import getAgendaModalFields from '../utils/agenda-modal-fields.js';
import { useModalStack } from '../hooks/useModalStack.js';
import {
  useOpenRecordFromUrl,
  useHighlightedSessionIds,
  URL_RECORD_PARAMS,
} from '../hooks/useOpenRecordFromUrl.js';
import { useAddToCalendarScript } from '../hooks/useAddToCalendarScript.js';
import {
  firstDatedHighlight,
  findAgendaDayIndex,
  scheduleScrollSessionIntoView,
} from '../utils/scroll-session-into-view.js';
import Footer from '../shared/Footer.jsx';
import Loading from '../shared/Loading.jsx';
import SubsessionModal from '../shared/SubsessionModal.jsx';
import { getSubsessionDataForModal } from '../utils/subsession-modal.js';
import { useI18n } from '../i18n/index.jsx';

// Layout constants for overlapping session cards
const GAP_PX = 2;           // pixel gap between side-by-side overlapping cards
const MIN_CARD_WIDTH_PX = 150; // minimum card width (px) when sessions overlap

export const generateDayHours = dayData => {
  const startHour = Math.max(0, dayData?.minHour - 1);
  const endHour = Math.min(23, dayData?.maxHour + 1);
  const hours = [];

  for (let hour = startHour; hour <= endHour; hour++) {
    hours.push({ hour, timeString: hourAxisLabel(hour, dayData?.hourLabels) });
  }

  return { hours, startHour, endHour };
};

/**
 * @param {import('../types/async-data.js').AsyncDataResponse<import('../types/async-data.js').AgendaScopeData>} data
 * @param {boolean} loading
 */
const Agenda = ({ data, loading }) => {
  const agendaData = data?.data;
  const primaryColor = data?.primaryColor;
  const eventName = data?.eventName;
  const { t } = useI18n();
  const { speakersById = {}, sessionsById = {} } = data?.data || {};
  const [currentDateIndex, setCurrentDateIndex] = useState(0);
  const scrollWrapperRef = useRef(null);
  const { visibleModal, openModal, closeModal } = useModalStack();
  const useModals = data?.useModals || false;

  useOpenRecordFromUrl({
    ready: !loading && useModals,
    shortcuts: [
      {
        param: URL_RECORD_PARAMS.SESSION,
        modalType: MODAL_TYPES.SESSION,
        exists: id => Boolean(sessionsById[id]),
      },
    ],
    openModal,
  });
  const highlightedSessionIds = useHighlightedSessionIds({ ready: !loading });

  // SB-8207 — first dated highlight; jump day then scroll the inner scroller.
  useEffect(() => {
    if (loading || !highlightedSessionIds.size) return;
    const pick = firstDatedHighlight(highlightedSessionIds, id =>
      findAgendaDayIndex(agendaData, id)
    );
    if (!pick) return;

    if (pick.dayIndex >= 0) {
      if (pick.dayIndex !== currentDateIndex) {
        setCurrentDateIndex(pick.dayIndex);
        return;
      }
      scheduleScrollSessionIntoView(() => scrollWrapperRef.current, pick.id);
      return;
    }

    if (useModals && sessionsById[pick.id]) {
      openModal(MODAL_TYPES.SESSION, pick.id);
    }
  }, [
    loading,
    highlightedSessionIds,
    agendaData,
    currentDateIndex,
    useModals,
    sessionsById,
    openModal,
  ]);

  const visibleAgendaFields = getVisibleFieldIdsOfType(data?.groupedFields, WIDGET_TYPES.AGENDA);
  const isDarkTheme = data?.isDarkTheme || false;
  const visibleSpeakerFields = getVisibleFieldIdsOfType(data?.groupedFields, WIDGET_TYPES.SPEAKERS);
  const visibleSpeakerFieldsById = getVisibleFieldsOfTypeById(data?.groupedFields, WIDGET_TYPES.SPEAKERS);
  const { ids: visibleSessionFields, byId: visibleSessionFieldsById } = getAgendaModalFields(data?.groupedFields);
  const showAddToCalendar = agendaData?.filterOptions?.show_add_to_calendar || false;
  const { isLoaded: isAddToCalendarScriptLoaded } = useAddToCalendarScript(showAddToCalendar);

  const navigateDate = direction => {
    if (direction === 'next' && currentDateIndex < agendaData?.orderedDateKeys?.length - 1) {
      setCurrentDateIndex(currentDateIndex + 1);
    } else if (direction === 'prev' && currentDateIndex > 0) {
      setCurrentDateIndex(currentDateIndex - 1);
    }
  };

  // Event handlers for modals
  const handleOpenSession = sessionId => {
    openModal(MODAL_TYPES.SESSION, sessionId);
  };

  const handleSpeakerClick = speakerId => {
    openModal(MODAL_TYPES.SPEAKER, speakerId);
  };

  // SB-8856: subsession rows push a subsession detail modal.
  const handleSubsessionClick = subsessionId => {
    openModal(MODAL_TYPES.SUBSESSION, subsessionId);
  };

  const handleCloseModal = (type, id) => {
    closeModal(type, id);
  };

  const isPrevDayDisabled = currentDateIndex === 0;
  const isNextDayDisabled = currentDateIndex === agendaData?.orderedDateKeys?.length - 1;

  const currentDateKey = agendaData?.orderedDateKeys[currentDateIndex];
  const currentDayData = agendaData?.tablesByDay[currentDateKey];
  const { hours: dayHours, startHour } = generateDayHours(currentDayData);

  // Pre-compute per-column min-widths so headers and columns stay in sync when sessions overlap.
  // Formula: N cards × MIN_CARD_WIDTH_PX + (N-1) gaps — exactly fits all cards with no overhang.
  const columnMinWidths = (currentDayData?.locations || []).map(locationName => {
    const locationKey = locationName === null ? 'null' : locationName;
    const sessions = currentDayData?.sessionsByLocation?.[locationKey] || [];
    const grouped = groupOverlappingSessions(sessions);
    const maxOverlap = grouped.reduce((max, s) => Math.max(max, s.overlapTotal || 1), 1);
    return maxOverlap > 1 ? maxOverlap * MIN_CARD_WIDTH_PX + (maxOverlap - 1) * GAP_PX : undefined;
  });

  const hasNoSessions = Boolean(agendaData) && !(agendaData.orderedDateKeys || []).length;

  if (!agendaData && loading) {
    return (
      <div className="agenda__loading">
        <Loading size="large" colorVariant="primary" text={t('loading_data')} />
      </div>
    );
  }

  return (
    <div
      className={`agenda ${isDarkTheme ? 'theme-dark' : ''}`}
      style={primaryColor ? { '--color-primary': primaryColor } : {}}
    >
      {/* Header */}
      <Header eventName={eventName} primaryColor={primaryColor} hidden={Boolean(data?.hideHeader)} />

      {/* Date Navigation */}
      {!hasNoSessions && (
        <div className="agenda__date-nav">
          <div className="agenda__date-nav-container">
            <div className="agenda__date-controls">
              {!isPrevDayDisabled && (
                <button className="agenda__nav-button" disabled={isPrevDayDisabled} onClick={() => navigateDate('prev')}>
                  <ChevronLeft className="sb-flip-rtl" />
                </button>
              )}
              <h2 className="agenda__date-title">{currentDayData?.dayTitle}</h2>
              {!isNextDayDisabled && (
                <button className="agenda__nav-button" disabled={isNextDayDisabled} onClick={() => navigateDate('next')}>
                  <ChevronRight className="sb-flip-rtl" />
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* SB-10252: Included-sessions rules that match nothing publish no
          sessions; say so instead of drawing an empty time grid. */}
      {hasNoSessions && <div className="agenda__no-data">{t('no_sessions_available')}</div>}

      {/* Agenda Grid */}
      {!hasNoSessions && (
        <div className="agenda__grid-container">
          <div className="agenda__grid">
            {/* Time Grid with Sessions — also contains sticky location headers so both scroll together */}
            <div className="agenda__time--scrollable" ref={scrollWrapperRef}>
              {/* Location Headers — sticky so they stay visible on vertical scroll */}
              <div className="agenda__location-headers">
                <div className="agenda__time-label-header"></div>
                {currentDayData?.locations?.map((locationName, index) => (
                  <div
                    key={locationName || `location-${index}`}
                    className="agenda__location-header"
                    style={columnMinWidths[index] ? { minWidth: `${columnMinWidths[index]}px` } : undefined}
                  >
                    <h3 className="agenda__location-name">{locationName || t('unassigned')}</h3>
                  </div>
                ))}
              </div>

              <div className="agenda__time-grid">
                {/* Time labels */}
                <div className="agenda__time-labels">
                  {dayHours.map(({ hour, timeString }) => (
                    <div key={hour} className="agenda__time-slot">
                      {timeString}
                    </div>
                  ))}
                </div>

                {/* Grid content with individual columns */}
                {currentDayData?.locations?.map((locationName, index) => {
                  // Get sessions for this location and group overlapping ones
                  // Handle null location keys (null value becomes "null" string in sessionsByLocation)
                  const locationKey = locationName === null ? 'null' : locationName;
                  const locationSessions = currentDayData?.sessionsByLocation[locationKey] || [];
                  const groupedSessions = groupOverlappingSessions(locationSessions);

                  return (
                    <div
                      key={`location-${locationName}-${index}`}
                      className="agenda__location-column"
                      style={columnMinWidths[index] ? { minWidth: `${columnMinWidths[index]}px` } : undefined}
                    >
                      {/* Grid lines for this column */}
                      {dayHours.map(({ hour }) => (
                        <div key={hour} className="agenda__grid-line" />
                      ))}

                      {/* Sessions for this column */}
                      {groupedSessions.map((session, sessionIndex) => {
                        const startTime = session.start_time;
                        const endTime = session.end_time;
                        const position = getSessionPosition(startTime, endTime, startHour);

                        // Calculate the width and left position based on overlap info.
                        // When sessions overlap, use pixel-based placement so cards fit the column exactly:
                        //   each slot = MIN_CARD_WIDTH_PX wide, separated by GAP_PX.
                        // When there is no overlap, fill the full column width as before.
                        const hasOverlap = session.overlapTotal > 1;
                        const cardWidth = hasOverlap
                          ? `${MIN_CARD_WIDTH_PX}px`
                          : `${session.width || 100}%`;
                        const cardLeft = hasOverlap
                          ? `${session.overlapIndex * (MIN_CARD_WIDTH_PX + GAP_PX)}px`
                          : `${session.leftOffset || 0}%`;

                        return (
                          <SessionCard
                            key={session.id}
                            session={session}
                            isHighlighted={highlightedSessionIds.has(session.id)}
                            visibleFields={visibleAgendaFields}
                            primaryColor={primaryColor}
                            onClick={handleOpenSession}
                            sessionIndex={sessionIndex}
                            style={{
                              position: 'absolute',
                              top: `${position.top}px`,
                              height: `${position.height}px`,
                              insetInlineStart: cardLeft,
                              width: cardWidth,
                            }}
                          />
                        );
                      })}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Session Modal */}
      {useModals && visibleModal?.type === MODAL_TYPES.SESSION && (
        <SessionModal
          visibleSpeakerFields={visibleSpeakerFields}
          sessionData={getSessionDataForModal(sessionsById[visibleModal.id], speakersById)}
          onClose={() => handleCloseModal(MODAL_TYPES.SESSION, visibleModal.id)}
          onBack={() => handleCloseModal(MODAL_TYPES.SESSION, visibleModal.id)}
          onSpeakerClick={handleSpeakerClick}
          onSubsessionClick={handleSubsessionClick}
          primaryColor={primaryColor}
          visibleSessionFields={visibleSessionFields}
          visibleSessionFieldsById={visibleSessionFieldsById}
          showAddToCalendar={showAddToCalendar && isAddToCalendarScriptLoaded}
        />
      )}

      {/* Subsession Modal (SB-8856) */}
      {useModals && visibleModal?.type === MODAL_TYPES.SUBSESSION && (
        <SubsessionModal
          visibleSpeakerFields={visibleSpeakerFields}
          subsessionData={getSubsessionDataForModal(visibleModal.id, sessionsById, speakersById)}
          onClose={() => handleCloseModal(MODAL_TYPES.SUBSESSION, visibleModal.id)}
          onBack={() => handleCloseModal(MODAL_TYPES.SUBSESSION, visibleModal.id)}
          onSpeakerClick={handleSpeakerClick}
          primaryColor={primaryColor}
          visibleSessionFields={visibleSessionFields}
          visibleSessionFieldsById={visibleSessionFieldsById}
        />
      )}

      {/* Speaker Modal */}
      {useModals && visibleModal?.type === MODAL_TYPES.SPEAKER && (
        <SpeakerModal
          speakerData={getSpeakerDataForModal(visibleModal.id, speakersById, Object.values(sessionsById))}
          onClose={() => handleCloseModal(MODAL_TYPES.SPEAKER, visibleModal.id)}
          onBack={() => handleCloseModal(MODAL_TYPES.SPEAKER, visibleModal.id)}
          onSpeakerClick={handleSpeakerClick}
          onSessionClick={handleOpenSession}
          visibleSpeakerFields={visibleSpeakerFields}
          visibleSpeakerFieldsById={visibleSpeakerFieldsById}
        />
      )}

      {/* Footer */}
      <Footer hidden={Boolean(data?.hideFooter)} />
    </div>
  );
};

export default Agenda;
